import type { LearningsStatus } from '@@/api/learnings/learnings-status';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useClient } from 'hooks/useClient';
import { apiErrorMessage } from '../api-error';

type UseLearningsBackfillParams = {
  websiteId: number;
  /** The saved switch: the status endpoint has nothing to say about a website with learnings off. */
  enabled: boolean;
};

/** Credits shown as a round figure, rounded up, since a customer decides on it. */
export const roundEstimate = (credits: number): number => (credits <= 0 ? 0 : Math.ceil(credits / 10) * 10);

/**
 * "Write learnings for past tests", in a website's settings. Turning learnings on covers only the
 * tests that end from then on, because every card costs AI credits; the website's earlier tests
 * wait for this confirmed action. The estimate is the status endpoint's per-card figure times the
 * tests it would write.
 *
 * Shares LEARNINGS_STATUS with the Library's indicator, so confirming here starts that indicator
 * too.
 */
export const useLearningsBackfill = ({ websiteId, enabled }: UseLearningsBackfillParams) => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const queryKey = ['LEARNINGS_STATUS', websiteId];
  const [confirming, setConfirming] = useState(false);

  const { data } = useQuery<LearningsStatus>({
    queryKey,
    enabled,
    queryFn: () => apiClient.get<LearningsStatus>(`/learnings/${websiteId}/status`),
  });

  const start = useMutation({
    mutationFn: () => apiClient.post(`/learnings/${websiteId}/backfill`, {}),
    onSuccess: () => {
      setConfirming(false);
      toast.success('Writing learnings for past tests. The Library shows how far it has got.');
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => toast.error(apiErrorMessage(error, 'Could not start writing learnings.')),
  });

  const past = data?.past ?? 0;

  return {
    past,
    estimate: roundEstimate(past * (data?.creditsPerCard ?? 0)),
    creditsPerCard: Math.round(data?.creditsPerCard ?? 0),
    paused: data?.paused ?? null,
    confirming,
    askToConfirm: () => setConfirming(true),
    cancel: () => setConfirming(false),
    // onError has already said why; caught here so the dialog's await does not rethrow it.
    confirm: () => start.mutateAsync().catch(() => undefined),
  };
};
